import { registerBlock, registerRelationship } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

// ─── The test itself ─────────────────────────────────────────────────

const test: ContentBlock<"test"> = {
  id: makeId("test-semen-analysis"),
  kind: "test",
  tags: ["semen-analysis", "testing", "his", "sperm", "at-home", "pre-cycle"],
  title: "Semen analysis",
  subtitle: "At-home collection kits exist  - or quick clinic visit",
  icon: "🔬",
  body: {
    testName: "Semen Analysis",
    category: "semen",
    whatItMeasures:
      "A semen analysis can report total sperm count, concentration, morphology, progressive and non-progressive motility, agglutination and aggregation, immotile sperm, pH, and volume. Depending on the selected panel, the same semen workup can also include antisperm antibodies, sperm DNA fragmentation, and sperm DNA methylation. It establishes a male fertility baseline and helps decide whether lifestyle changes, reproductive urology, IUI, IVF, ICSI, or additional sperm testing should be considered.",
    howItWorks:
      "Sample collection via at-home kit mailed to a lab, or collected at a clinic. Abstain 2-5 days before. Core parameters are evaluated on the semen sample, while selected panels may add antisperm antibody, DNA fragmentation, or DNA methylation assays. Results should be interpreted with a clinician because sperm numbers vary naturally, and a repeat test is often useful before making major decisions.",
    quickFacts: [
      { kind: "sample", value: "Semen" },
      { kind: "time", value: "Home kit or clinic collection" },
      { kind: "prep", value: "Abstain 2-5 days" },
      { kind: "results", value: "3-5 business days" },
    ],
    turnaroundTime: "3-5 business days",
    costRange: { low: 100, high: 200 },
    atHome: true,
    clinicRequired: false,
    expectations: {
      canExpect: [
        "A baseline read on count, motility, morphology, and volume",
        "A repeatable test that can show whether changes over a full sperm-production cycle are helping",
        "Results that can guide whether to consider reproductive urology, IUI, IVF, ICSI, or sperm DNA fragmentation testing",
        "Optional advanced panel results for antisperm antibodies, sperm DNA fragmentation, or sperm DNA methylation",
      ],
      shouldNotExpect: [
        "One result to explain the entire fertility picture by itself",
        "Morphology to change as quickly or reliably as count and motility",
        "Every panel to include every advanced assay",
      ],
    },
    considerations: [
      "If optimizing sperm health, count and motility are often rechecked after about 90 days because sperm development takes roughly 72-90 days.",
      "Morphology is usually the least responsive semen parameter, and many reproductive endocrinologists focus more on total motile count and motility.",
      "Results interpretation with a fertility specialist or reproductive urologist can be useful when values are abnormal or the couple has recurrent loss or failed ART cycles.",
      "Advanced assays such as antisperm antibodies, sperm DNA fragmentation, and sperm DNA methylation are panel-dependent and should be interpreted alongside the core semen parameters and clinical history.",
      "AUA/ASRM does not recommend sperm DNA fragmentation testing as part of the initial infertility evaluation; its usefulness depends on the clinical context and the assay used.",
    ],
    dataPoints: [
      makeId("dp-sperm-count"),
      makeId("dp-sperm-concentration"),
      makeId("dp-sperm-motility"),
      makeId("dp-sperm-non-progressive-motility"),
      makeId("dp-sperm-morphology"),
      makeId("dp-sperm-agglutination"),
      makeId("dp-sperm-immotile"),
      makeId("dp-semen-ph"),
      makeId("dp-semen-volume"),
      makeId("dp-antisperm-antibodies"),
      makeId("dp-sperm-dna-fragmentation"),
      makeId("dp-sperm-dna-methylation"),
    ],
    section: "semen-analysis",
  },
  sources: [
    {
      title:
        "WHO Laboratory Manual for the Examination and Processing of Human Semen, 6th Edition",
      authors: "World Health Organization",
      journal: "WHO Publications",
      year: 2021,
      url: "https://www.who.int/publications/i/item/9789240030787",
      evidenceLevel: "guideline",
    },
    {
      title:
        "Diagnosis and Treatment of Infertility in Men: AUA/ASRM Guideline (Part I)",
      authors: "Schlegel PN et al.",
      journal: "Fertility and Sterility / Journal of Urology",
      year: 2020,
      url: "https://www.asrm.org/practice-guidance/practice-committee-documents/diagnosis-and-treatment-of-infertility-in-men-auaasrm-guideline-part-i-2020/",
      evidenceLevel: "guideline",
    },
  ],
  readTimeMinutes: 4,
};

registerBlock(test);

// ─── Relationships ───────────────────────────────────────────────────

registerRelationship({
  from: makeId("test-semen-analysis"),
  to: makeId("dp-sperm-count"),
  type: "data-for",
});

registerRelationship({
  from: makeId("test-semen-analysis"),
  to: makeId("dp-sperm-concentration"),
  type: "data-for",
});

registerRelationship({
  from: makeId("test-semen-analysis"),
  to: makeId("dp-sperm-motility"),
  type: "data-for",
});

registerRelationship({
  from: makeId("test-semen-analysis"),
  to: makeId("dp-sperm-non-progressive-motility"),
  type: "data-for",
});

registerRelationship({
  from: makeId("test-semen-analysis"),
  to: makeId("dp-sperm-morphology"),
  type: "data-for",
});

for (const dataPointId of [
  "dp-sperm-agglutination",
  "dp-sperm-immotile",
  "dp-semen-ph",
  "dp-semen-volume",
  "dp-antisperm-antibodies",
  "dp-sperm-dna-fragmentation",
  "dp-sperm-dna-methylation",
] as const) {
  registerRelationship({
    from: makeId("test-semen-analysis"),
    to: makeId(dataPointId),
    type: "data-for",
  });
}

// Link to sperm optimization (registered in optimization/sperm-optimization.ts)
registerRelationship({
  from: makeId("test-semen-analysis"),
  to: makeId("tip-sperm-optimization"),
  type: "related-to",
  label: "How to improve your results",
});
